import {
  convertToChosenInvestments,
  getResidentialAddressForEnabled,
  filterMailingAddressForEnabled,
  appTaxableInvestmentsToProfileInvestments,
  appTaxableAccountsToProfileAccounts,
  populateProfileAdditionalCustomers,
  populateAppAdditionalCustomers
} from '../../shared/services/util';
import { withDefault, APP_NAME, APP_VERSION } from '../../../core/util';
import {
  applicationStatusTypes,
  setTaxIDType,
  parsePhone,
  accountTypes,
  customerTypes,
  chargeTypes,
  mapAddressType,
  setRJCustomerPhones,
  getMailingAddressDifferentFlag
} from '../../shared/utils';
import { houseHoldSummitData } from '../../shared/summit.utils';
import { getCustomerHouseholdDataByTaxId } from '../../shared/services/household-service';

import {
  PartialStateStore,
  StateStore
} from '../../../core/types/application-state';
import { DeepPartialProfileArgs } from '../../../core/types/return-journey-profile';
import { isLouisianaResident } from '../../shared/testers';

export const parseTaxableTransferProfileAccountType = (
  accountType = '',
  chargeType = 'NEW'
): string => {
  if (chargeType === chargeTypes.TRANSFER) {
    return `TRANSFER_${accountType}`;
  }
  return accountType;
};

export const parseTaxableTransferAccountTypeFromProfile = (
  accountType = ''
): string => {
  if (accountType.includes('TRANSFER')) {
    return accountType.replace('TRANSFER_', '');
  }

  return accountType;
};

export const populateAppJointOwners = (
  additionalCustomers: any[] | undefined
): any =>
  withDefault(additionalCustomers, []).map(person => ({
    name: {
      firstName: person.firstName,
      middleName: withDefault(person.middleName, ''),
      lastName: person.lastName
    },
    dob: person.dateOfBirth,
    taxID: person.taxID,
    taxIDType: setTaxIDType(person.citizenship),
    citizenship: person.citizenship,
    residentialAddress: mapAddressType(person.residentialAddress)
  }));

export const populateAppMinorOwners = (
  additionalCustomers: any[] | undefined
): any => {
  if (!additionalCustomers || !additionalCustomers?.length) return {};

  const minorCustomer = additionalCustomers.find(
    addCust => addCust.customerType === customerTypes.MINOR
  );
  return minorCustomer
    ? {
        name: {
          firstName: minorCustomer.firstName,
          middleName: withDefault(minorCustomer.middleName, ''),
          lastName: minorCustomer.lastName
        },
        dob: minorCustomer.dateOfBirth,
        taxID: minorCustomer.taxID,
        taxIDType: setTaxIDType(minorCustomer.citizenship),
        citizenship: minorCustomer.citizenship,
        residentialAddress: mapAddressType(minorCustomer.residentialAddress)
      }
    : {};
};

// following two parse information functions are to handle defaulting
// information when input values are no longer accessible
// i.e. in the case of a modal event
export const parseJointOwnerInformation = (people, jointOwners): any => {
  return people.map((jo, index) => ({
    name: {
      firstName: withDefault(
        jo.jointOwnerFirstName,
        jointOwners?.[index]?.name?.firstName
      ),
      middleName:
        withDefault(
          jo.jointOwnerMiddleName,
          jointOwners?.[index]?.name?.middleName
        ) || '',
      lastName: withDefault(
        jo.jointOwnerLastName,
        jointOwners?.[index]?.name?.lastName
      )
    },
    taxID: withDefault(
      jo.jointOwnerSsn?.replace(/[^*0-9]/g, ''),
      jointOwners?.[index]?.taxID
    ),
    dob: withDefault(jo.jointOwnerDob, jointOwners?.[index]?.dob),
    taxIDType: withDefault(
      setTaxIDType(jo.jointOwnerCitizenship),
      jointOwners?.[index]?.taxIDType
    ),
    citizenship:
      withDefault(
        jo.jointOwnerCitizenship,
        jointOwners?.[index]?.citizenship
      ) || ''
  }));
};

// following functions is to parse joint owners tax ID's
export const parseJointOwnerTaxIDs = (people, jointOwners): any => {
  return people.map((jo, index) =>
    withDefault(
      jo.jointOwnerSsn?.replace(/[^*0-9]/g, ''),
      jointOwners?.[index]?.taxID
    )
  );
};

export const parseAdditionalCustomers = (persons, jointOwners): any =>
  persons?.map((person, idx) => ({
    customerType: 'JOINT_TENANT',
    name: {
      firstName: withDefault(
        person.jointOwnerFirstName,
        jointOwners?.[idx]?.name?.firstName
      ),
      middleName:
        withDefault(
          person.jointOwnerMiddleName,
          jointOwners?.[idx]?.name?.middle
        ) || '',
      lastName: withDefault(
        person.jointOwnerLastName,
        jointOwners?.[idx]?.name?.lastName
      )
    },
    dob: withDefault(person.jointOwnerDob, jointOwners?.[idx]?.dob),
    taxID: withDefault(
      person.jointOwnerSsn?.replace(/[^*0-9]/g, ''),
      jointOwners?.[idx]?.taxID
    ),
    citizenship:
      withDefault(
        person.jointOwnerCitizenship,
        jointOwners?.[idx]?.citizenship
      ) || '',
    sameAddress: person.jointOwnerSameAddress,
    residentialAddress: person.residentialAddress
  }));

const getSummitDataForUgmaRJ = async (
  accountType: string | undefined,
  additionalCustomers: any[] | undefined
): Promise<any> => {
  let summitData = {};

  if (
    accountType?.includes(accountTypes.UGMAUTMA) &&
    additionalCustomers?.[0]?.taxID
  ) {
    const minorHHdata = await getCustomerHouseholdDataByTaxId({
      firstName: additionalCustomers?.[0]?.firstName,
      lastName: additionalCustomers?.[0]?.lastName,
      dateOfBirth: additionalCustomers?.[0]?.dateOfBirth,
      taxId: additionalCustomers?.[0]?.taxID.replace(/[^*0-9]/g, '')
    });
    summitData = houseHoldSummitData(minorHHdata);
  }

  return summitData;
};

export const convertTaxableAppDataToProfileData = (
  store: StateStore
): DeepPartialProfileArgs => {
  const {
    app,
    chargeType,
    chosenInvestments,
    progressStepText,
    additionalCustomers = []
  } = store;

  const newInvestments = appTaxableInvestmentsToProfileInvestments(
    chosenInvestments
  );

  const existingAccounts = appTaxableAccountsToProfileAccounts(
    app?.existingAccounts
  );

  // TODO [2020-05-25]: Let's make this a util to combine new + existing investments for profile service
  const investmentAllocations = [...existingAccounts, ...newInvestments];

  return {
    applicationID: withDefault(app?.profileApplicationID, ''),
    emailAddress: app?.customer?.primaryEmailAddress,
    applicationStatus: withDefault(
      app?.applicationStatus,
      applicationStatusTypes.IN_PROGRESS
    ),
    investmentAllocations,
    accountType: parseTaxableTransferProfileAccountType(
      store?.accountType,
      chargeType
    ) /* US587888 - MF Events - Taxable vs Taxable Transfer differentiation */,
    chargeType: withDefault(store.chargeType, chargeTypes.NEW),
    fundingType: app?.fundingMethodType,
    investAmount: withDefault(store?.amount, 0),
    firstName: app?.customer?.name?.firstName,
    lastName: app?.customer?.name?.lastName,
    middleName: withDefault(app?.customer?.name?.middleName, ''),
    residentialAddress: {
      street1: app?.customer?.residentialAddress?.street1,
      street2: app?.customer?.residentialAddress?.street2,
      state: app?.customer?.residentialAddress?.state,
      zip: app?.customer?.residentialAddress?.zip,
      city: app?.customer?.residentialAddress?.city
    },
    mailingAddress: {
      street1: app?.customer?.mailingAddress?.street1,
      street2: app?.customer?.mailingAddress?.street2,
      state: app?.customer?.mailingAddress?.state,
      zip: app?.customer?.mailingAddress?.zip,
      city: app?.customer?.mailingAddress?.city
    },
    citizenship: withDefault(app?.customer?.citizenship, 'UNKNOWN'),
    dateOfBirth: app?.customer?.dob,
    taxID: withDefault(app?.customer?.taxID, ''),
    phoneNumbers:
      app?.customer?.phones?.map(({ areaCode, exchange, lastFour, type }) => ({
        phoneNumber: `(${areaCode}) ${exchange}-${lastFour}`,
        phoneNumberType: type
      })) || [],
    additionalCustomers: populateProfileAdditionalCustomers(
      additionalCustomers
    ),
    beneficiaries: app?.beneficiaries,
    investmentSelectionPath: app?.investmentMethod,
    residentialAddressDifferentFromMailing: withDefault(
      store?.mailingAddressDifferent,
      false
    ),
    // ToDo: Add termIDs on the review page submission
    termsIDs: withDefault(
      app?.termsIDs?.map(id => id?.toString()),
      []
    ),
    consumerVersion: APP_VERSION,
    consumerName: APP_NAME,
    progressStepText,
    progressStep: withDefault(store?.progressStep, 0),
    governingState: app?.governingState
  };
};

export const convertTaxableProfileDataToAppData = async (
  profile: DeepPartialProfileArgs,
  store: PartialStateStore
): Promise<PartialStateStore> => {
  // account for logged in user customer data
  // customer data should take precedence over journey profile customer data
  const { accountType, additionalCustomers } = profile;
  const { app, enabled } = store;
  const summitData = await getSummitDataForUgmaRJ(
    accountType,
    additionalCustomers
  );
  const phones = setRJCustomerPhones(
    profile?.phoneNumbers,
    app?.customer?.phones
  );

  return {
    accountType: parseTaxableTransferAccountTypeFromProfile(
      profile?.accountType
    ) /* US587888 - MF Events - Taxable vs Taxable Transfer differentiation */,
    chargeType: withDefault(profile?.chargeType, chargeTypes.NEW),
    amount: profile?.investAmount,
    app: {
      customer: enabled
        ? {
            ...app?.customer,
            citizenship: profile?.citizenship,
            taxIDType: setTaxIDType(profile?.citizenship),
            residentialAddress: getResidentialAddressForEnabled(
              app?.customer?.residentialAddress,
              profile?.residentialAddress
            ),
            // pull email, phone and mailing address from profile service to
            // account for RPS only users submitting via FONASU RJ
            // since the customer call while doing RJ is not pulling them currently
            mailingAddress: withDefault(
              profile?.mailingAddress,
              filterMailingAddressForEnabled(app?.customer?.mailingAddress)
            ),
            primaryEmailAddress: withDefault(
              profile?.emailAddress,
              app?.customer?.primaryEmailAddress
            ),
            phones
          }
        : {
            name: {
              firstName: profile?.firstName,
              middleName: withDefault(profile?.middleName, ''),
              lastName: profile?.lastName
            },
            citizenship: profile?.citizenship,
            dob: profile?.dateOfBirth,
            taxID: profile?.taxID,
            taxIDType: setTaxIDType(profile?.citizenship),
            residentialAddress: {
              street1: profile?.residentialAddress?.street1,
              street2: profile?.residentialAddress?.street2,
              state: profile?.residentialAddress?.state,
              zip: profile?.residentialAddress?.zip,
              city: profile?.residentialAddress?.city
            },
            mailingAddress: {
              street1: profile?.mailingAddress?.street1,
              street2: profile?.mailingAddress?.street2,
              state: profile?.mailingAddress?.state,
              zip: profile?.mailingAddress?.zip,
              city: profile?.mailingAddress?.city
            },
            primaryEmailAddress: profile?.emailAddress,
            // [{phoneNumberType: string, phoneNumber: string}]
            // needs to be converted to ->
            // [{ areaCode: string, exchange: string, lastFour: string, type: string }]
            //
            // TODO [2020-06-20]: This is a big pain why can't backend use same format
            phones: profile?.phoneNumbers?.map(
              ({ phoneNumberType: type = '', phoneNumber = '' }) => ({
                ...parsePhone(phoneNumber),
                type
              })
            )
          },
      newInvestments: profile?.investmentAllocations?.map(
        ({ ticker, trpCode, fundingAmount, fundingAmountPercentage }) => ({
          ticker,
          trpCode,
          fundingAmount,
          percent: fundingAmountPercentage
        })
      ),
      investmentMethod: profile?.investmentSelectionPath,
      fundingMethodType: profile?.fundingType,
      profileApplicationID: profile?.applicationID,
      termsIDs: withDefault(
        profile?.termsIDs?.map(id => id?.toString()),
        []
      ),
      ...(profile?.chargeType !== chargeTypes.TRANSFER && {
        beneficiaries: profile?.beneficiaries
      }),
      ...(profile.accountType?.includes(accountTypes.JOINT) && {
        jointOwners: populateAppJointOwners(profile?.additionalCustomers)
      }),
      ...(profile.accountType?.includes(accountTypes.UGMAUTMA) && {
        minor: populateAppMinorOwners(profile?.additionalCustomers),
        governingState: profile?.governingState
      })
    },
    additionalCustomers: populateAppAdditionalCustomers(
      profile?.additionalCustomers
    ),
    mailingAddressDifferent: getMailingAddressDifferentFlag(
      enabled,
      store,
      profile
    ),
    chosenInvestments: await convertToChosenInvestments(
      profile?.investmentAllocations,
      profile?.investmentSelectionPath
    ),
    summitData,
    progressStepText: profile?.progressStepText,
    progressStep: withDefault(profile?.progressStep, 0),
    consumerVersion: profile?.consumerVersion,
    consumerName: profile?.consumerName
  };
};

/**
 * Updates the submission payload by removing beneficiary data if the customer
 * or joint owners are residents of Louisiana. This is necessary because Transfer
 * on Death (TOD) designations are not allowed in Louisiana.
 *
 * @param submitPayload - The submission payload object containing customer and joint owner information.
 * @returns The updated submission payload with beneficiary data removed if the state of residence is Louisiana.
 */
export const setBenesBasedOnStateResidence = (submitPayload: any = {}): any => {
  const { customer, jointOwners = [] } = submitPayload;

  // if client updated their state of residence, to LA, anytime after contact information
  // purge beneficiary data from the submission payload; TODs are not allowed in LA
  if (isLouisianaResident(customer, jointOwners)) {
    const payload = submitPayload;
    delete payload.beneficiaries;
    return payload;
  }

  return submitPayload;
};
